/**
 * BulkActionBar.jsx
 * Appears when one or more KPI rows are selected.
 * Shows count and a dropdown of bulk actions.
 *
 * Props:
 *   count        {number}    — selected item count
 *   onAction     {fn(key)}   — called with action key
 *   onDeselect   {fn}        — clear selection
 */

import React, { useState, useRef, useEffect } from "react";
import { KpiIcon } from "./KpiIcons";
import { SecondaryButton } from "../../../../globalComponents/ui/Button";

const BULK_ACTIONS = [
  { key: "assign", icon: "👤", label: "Assign Data Owner" },
  { key: "removeDataOwner", icon: "👤", label: "Remove Data Owner" },
  { key: "reassign", icon: "📤", label: "Re Assign" },
  { key: "auditAssign", icon: "🔍", label: "Assign Auditor" },  // ← NEW
  // { key: "removeAuditor", icon: "🔍", label: "Remove Auditor" },  // ← NEW
  { key: "prefill", icon: "✅", label: "Prefill From Previous Year" },
  { key: "reminder", icon: "⏳", label: "Email Reminder" },
];


const BulkActionBar = ({ count = 0, onAction, onDeselect, hasPermission = () => true }) => {
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);


  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (count === 0) return null;

  return (
    <div
      className="kpi-bulk-bar"
      style={{
        background: "var(--ink)",
        padding: "10px 28px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      {/* Count + info */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 24, height: 24, borderRadius: "50%",
            background: "var(--accent)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 800, flexShrink: 0,
          }}
        >
          {count}
        </div>
        <span style={{ color: "#fff", fontWeight: 600, fontSize: 13, fontFamily: "var(--font-sans)" }}>
          item{count !== 1 ? "s" : ""} selected
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {/* Actions dropdown */}
      <div ref={dropRef} style={{ position: "relative" }}>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 16px",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            border: "1.5px solid rgba(255,255,255,0.2)",
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 12.5,
            fontFamily: "var(--font-sans)",
            cursor: "pointer",
            letterSpacing: "0.02em",
            transition: "all 0.15s",
          }}
        >
          Bulk Actions
          <KpiIcon name="chevron" size={12} color="#fff" />
        </button>

        {open && (
          <div className="kpi-dropdown-menu" style={{ minWidth: 220 }}>
            {BULK_ACTIONS.filter(a => {
              if (a.key === "assign") return hasPermission("data-owner.assign");
              if (a.key === "removeDataOwner") return hasPermission("data-owner.remove");
              if (a.key === "reassign") return hasPermission("data-owner.reassign");
              if (a.key === "auditAssign") return hasPermission("auditor.assign");
              if (a.key === "removeAuditor") return hasPermission("auditor.remove");
              if (a.key === "prefill") return hasPermission("prefill-from-previous-year.execute");
              if (a.key === "reminder") return hasPermission("email-reminder.send");
              return true;
            }).map((a) => (
              <div
                key={a.key}
                className={`kpi-dropdown-item${a.danger ? " danger" : ""}`}
                onClick={() => {
                  onAction?.(a.key);
                  setOpen(false);
                }}
              >
                <span style={{ fontSize: 15 }}>{a.icon}</span>
                <span>{a.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deselect */}
      <SecondaryButton
        onClick={onDeselect}
        style={{
          background: "rgba(255,255,255,0.1)",
          color: "rgba(255,255,255,0.7)",
          border: "none",
          height: 32,
          fontSize: 12,
        }}
      >
        Deselect All
      </SecondaryButton>
    </div>
  );
};

export default BulkActionBar;